import { get } from "aws-amplify/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import Select from "react-select";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { _s3GetURL, s3Upload } from "lib/awsLib";
import AppendixList from "./AppendixList";
import "./IntroPgXDetail.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

const columnHelper = createColumnHelper();

function normalizeCityscopeOptions(rows) {
  return (rows || [])
    .map((row) => {
      const value = row.value ?? row.cityscope ?? row.cityscope_code ?? "";
      const label = row.label ?? row.cityscope_name ?? row.cityscope ?? String(value);
      return {
        value: String(value),
        label,
        cityscope_name: row.cityscope_name ?? label,
      };
    })
    .filter((row) => row.value);
}

function normalizeAppendixRows(rows) {
  return (rows || []).map((row) => ({
    ...row,
    appendix: row.appendix ?? row.value ?? row.label ?? "",
    issue_date: row.issue_date ?? "",
    group: row.group ?? row.cityscope ?? "",
  }));
}

function formatIssueDateQuarter(issueDate) {
  if (!issueDate) {
    return "";
  }

  const parsed = new Date(issueDate);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const month = parsed.getMonth();
  const quarter = Math.floor(((month + 3) % 12) / 3) + 1;
  return `${parsed.getFullYear()} 0${quarter}`;
}

function buildAppendixDropdownOptions(rows, searchByQuarter) {
  if (!searchByQuarter) {
    return rows.map((row) => ({
      value: String(row.appendix ?? ""),
      label: String(row.appendix ?? ""),
    }));
  }

  const quarterMap = new Map();
  rows.forEach((row) => {
    const quarter = formatIssueDateQuarter(row.issue_date);
    if (!quarter) {
      return;
    }

    const existing = quarterMap.get(quarter);
    const issueDateTime = new Date(row.issue_date).getTime();
    if (!existing || issueDateTime > existing.issueDateTime) {
      quarterMap.set(quarter, {
        value: quarter,
        label: quarter,
        issueDateTime,
      });
    }
  });

  return Array.from(quarterMap.values())
    .sort((a, b) => b.issueDateTime - a.issueDateTime)
    .map(({ value, label }) => ({ value, label }));
}

function buildConsolidatedTotals(rawSales, classifications) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const classificationMapping = {};
  (classifications || []).forEach((item) => {
    classificationMapping[item.classification] = item.make_detail_line ? item.classification : "Other";
  });

  const appendixTotalsMap = new Map();

  (rawSales || []).forEach((sale) => {
    const appendixKey = String(sale.appendix ?? "");
    if (!appendixKey) {
      return;
    }

    const salePrice = Number(String(sale.sale_price ?? "").replace(/[^0-9.-]+/g, "")) || 0;

    if (!appendixTotalsMap.has(appendixKey)) {
      const issueDate = sale.issue_date ? new Date(sale.issue_date) : null;
      appendixTotalsMap.set(appendixKey, {
        appendix: appendixKey,
        saleCount: 0,
        saleTotalPrice: 0,
        issue_date: issueDate && !isNaN(issueDate)
          ? `${months[issueDate.getMonth()]}-${String(issueDate.getFullYear()).slice(-2)}`
          : sale.issue_date ?? "",
      });
    }

    const current = appendixTotalsMap.get(appendixKey);
    current.saleCount += 1;
    current.saleTotalPrice += salePrice;
  });

  const appendixTotals = Array.from(appendixTotalsMap.values())
    .sort((a, b) => Number(a.appendix) - Number(b.appendix))
    .map((row, index) => ({
      ...row,
      group: (index + 1) / 4 <= 1 ? 1 : 2,
    }));

  const classificationRows = (classifications || [])
    .filter((item) => classificationMapping[item.classification] !== "Other")
    .concat([
      {
        classification: "Other",
        make_detail_line: false,
        order: 100,
        detail_strata: false,
        alternate_text: "",
      },
    ])
    .sort((a, b) => Number(a.order ?? 100) - Number(b.order ?? 100));

  return {
    appendixTotals,
    classificationRows,
    classificationMapping,
  };
}

function toNumber(value) {
  return Number(String(value ?? "").replace(/[^0-9.-]+/g, "")) || 0;
}

function isTruthy(value) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return Boolean(value);
}

function upsertSaleMetric(target, sale) {
  const appendix = String(sale.appendix ?? "");
  if (!appendix) {
    return;
  }
  const existing = target.find((item) => String(item.appendix ?? "") === appendix);
  if (existing) {
    existing.saleCount += 1;
    existing.saleTotalPrice += toNumber(sale.sale_price);
    return;
  }
  target.push({
    appendix,
    saleCount: 1,
    saleTotalPrice: toNumber(sale.sale_price),
  });
}

function upsertStrataMetric(target, sale) {
  const appendix = String(sale.appendix ?? "");
  if (!appendix) {
    return;
  }
  const area = Number(sale.unit_area) || 0;
  if (!area) {
    return;
  }
  const price = toNumber(sale.sale_price);
  const existing = target.find((item) => String(item.appendix ?? "") === appendix);
  if (existing) {
    const nextCount = existing.strataSaleCount + 1;
    existing.strataPricePerSqm = ((existing.strataPricePerSqm * existing.strataSaleCount) + price / area) / nextCount;
    existing.strataSaleCount = nextCount;
    existing.strataSalePrice += price;
    existing.strataUnitArea += area;
    return;
  }
  target.push({
    appendix,
    strataPricePerSqm: price / area,
    strataSaleCount: 1,
    strataSalePrice: price,
    strataUnitArea: area,
  });
}

function buildAppendixClassTotals(rawSales, classificationMapping) {
  const results = new Map();

  (rawSales || []).forEach((sale) => {
    const sourceClassification = sale.classification ?? "Other";
    const classification = classificationMapping[sourceClassification] ?? sourceClassification;
    if (!results.has(classification)) {
      results.set(classification, {
        classification,
        building_sales: [],
        unit_sales: [],
        strata_sales: [],
      });
    }

    const current = results.get(classification);
    const isStrata = sale.is_unit === "Strata";

    if (isStrata) {
      upsertSaleMetric(current.unit_sales, sale);

      const eligibleForStrataDetail =
        !isTruthy(sale.off_the_plan) &&
        !isTruthy(sale.related_parties) &&
        !isTruthy(sale.extraordinary) &&
        !isTruthy(sale.part_interest) &&
        !isTruthy(sale.multi_sale);

      if (eligibleForStrataDetail) {
        upsertStrataMetric(current.strata_sales, sale);
      }
      return;
    }

    upsertSaleMetric(current.building_sales, sale);
  });

  return Array.from(results.values());
}

async function resolveUploadedNewsletterUrl(uploadedPath, fileName) {
  const rawPath = String(uploadedPath || "").trim();
  const normalizedFileName = String(fileName || "").trim();

  const candidates = [
    rawPath,
    rawPath.replace(/^public\//, ""),
    rawPath.replace(/^private\//, ""),
    rawPath.replace(/^protected\//, ""),
    normalizedFileName ? `article/intropgx/${normalizedFileName}` : "",
    normalizedFileName,
  ].filter(Boolean);

  let lastError = null;
  for (const candidate of [...new Set(candidates)]) {
    try {
      const url = await _s3GetURL(candidate, 900, true);
      if (url) {
        return url;
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to resolve uploaded newsletter URL");
}

function getErrorStatusCode(error) {
  return (
    error?.$metadata?.httpStatusCode ||
    error?.statusCode ||
    error?.status ||
    null
  );
}

export default function IntroPgXDetail({ isAuthenticated }) {
  const createId = useMemo(() => createElementIdFactory("intropgx-detail"), []);
  const classificationTableIds = useMemo(
    () => createReusableTableElementIds(createElementIdFactory("intropgx-classification-list")),
    []
  );

  const [isLoading, setIsLoading] = useState(false);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [selectedCityscopeOptions, setSelectedCityscopeOptions] = useState([]);
  const [appendixOptions, setAppendixOptions] = useState([]);
  const [selectedAppendix, setSelectedAppendix] = useState("");
  const [rawSales, setRawSales] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [appendixTotals, setAppendixTotals] = useState([]);
  const [showAppendixNumber, setShowAppendixNumber] = useState(false);
  const [searchByQuarter, setSearchByQuarter] = useState(false);
  const [classificationMapping, setClassificationMapping] = useState({});
  const [hasSalesLoaded, setHasSalesLoaded] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [hasConsolidated, setHasConsolidated] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [generatedFileName, setGeneratedFileName] = useState("");
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const isMultiCityscopeSelection = selectedCityscopeOptions.length > 1;
  const selectedCityscope = useMemo(
    () => selectedCityscopeOptions.map((option) => option.value).join(","),
    [selectedCityscopeOptions]
  );

  const loadCityscopes = useCallback(async () => {
    try {
      const response = await get({
        apiName: "colin",
        path: "/cityscopes/dropdown",
      }).response;
      const json = await response.body.json();
      const options = normalizeCityscopeOptions(Array.isArray(json) ? json : []);
      setCityscopeOptions(options);
    } catch (error) {
      console.error("Failed to load cityscopes", error);
      setUi({ showAlert: true, errorMessage: "Failed to load cityscope options." });
    }
  }, []);

  const loadAppendices = useCallback(async () => {
    if (!selectedCityscope) {
      setAppendixOptions([]);
      setSelectedAppendix("");
      return;
    }

    try {
      const options = { queryParams: { cityscope: selectedCityscope } };

      const response = await get({
        apiName: "colin",
        path: "/appendix",
        options,
      }).response;

      const json = await response.body.json();
      const rows = normalizeAppendixRows(Array.isArray(json) ? json : []);
      const nextOptions = buildAppendixDropdownOptions(rows, searchByQuarter);

      setAppendixOptions(nextOptions);
      if (nextOptions.length > 0) {
        setSelectedAppendix(nextOptions[0].value);
      } else {
        setSelectedAppendix("");
      }
    } catch (error) {
      console.error("Failed to load appendices", error);
      setUi({ showAlert: true, errorMessage: "Failed to load newsletter appendices." });
      setAppendixOptions([]);
      setSelectedAppendix("");
    }
  }, [searchByQuarter, selectedCityscope]);

  const loadSalesSource = useCallback(async () => {
    if (!selectedCityscope) {
      setUi({ showAlert: true, errorMessage: "Please select a Cityscope first." });
      return;
    }

    if (!selectedAppendix) {
      setUi({ showAlert: true, errorMessage: `Please select a ${searchByQuarter ? "Quarter" : "Appendix"} first.` });
      return;
    }

    setIsLoading(true);
    setUi({ showAlert: false, errorMessage: "" });
    setHasConsolidated(false);
    try {
      const cityscopeParam = selectedCityscope
        ? `cityscope=${encodeURIComponent(selectedCityscope)}`
        : "";
      const selectionParam = searchByQuarter
        ? `quarter=${encodeURIComponent(selectedAppendix || "")}`
        : `appendix=${encodeURIComponent(selectedAppendix || "")}`;
      const queryString = [cityscopeParam, selectionParam].filter(Boolean).join("&");

      const response = await get({
        apiName: "colin",
        path: `/intropgx/all?${queryString}`,
      }).response;

      const json = await response.body.json();
      const sales = Array.isArray(json) ? json : [];
      setRawSales(sales);
      setHasSalesLoaded(true);
      setHasConsolidated(false);
      setGeneratedUrl("");
      setGeneratedFileName("");
      setGeneratedHtml("");

      const uniqueClassifications = [...new Set(sales.map((sale) => sale.classification).filter(Boolean))];
      setClassifications(
        uniqueClassifications.map((classification) => ({
          classification,
          make_detail_line: false,
          order: 100,
          detail_strata: false,
          alternate_text: "",
        }))
      );
    } catch (error) {
      console.error("Failed to load sales source", error);
      setUi({ showAlert: true, errorMessage: "Failed to load sales source." });
      setRawSales([]);
      setClassifications([]);
      setHasSalesLoaded(false);
      setHasConsolidated(false);
    } finally {
      setIsLoading(false);
    }
  }, [searchByQuarter, selectedAppendix, selectedCityscope]);

  const handleConsolidate = useCallback(() => {
    if (!hasSalesLoaded || rawSales.length === 0) {
      setUi({
        showAlert: true,
        errorMessage: "Please run Get Sales Source before Consolidate Other.",
      });
      return;
    }
    const consolidated = buildConsolidatedTotals(rawSales, classifications);
    setAppendixTotals(consolidated.appendixTotals);
    setClassifications(consolidated.classificationRows);
    setClassificationMapping(consolidated.classificationMapping);
    setHasConsolidated(true);
    setGeneratedUrl("");
    setGeneratedFileName("");
    setGeneratedHtml("");
  }, [classifications, hasSalesLoaded, rawSales]);

  const handleViewNewsFile = useCallback(async () => {
    if (!hasConsolidated || appendixTotals.length === 0) {
      setUi({
        showAlert: true,
        errorMessage: "Please run Get Sales Source and Consolidate Other before viewing the news file.",
      });
      return;
    }

    try {
      setIsLoading(true);
      setUi({ showAlert: false, errorMessage: "" });

      const group1Rows = appendixTotals.filter((row) => Number(row.group) === 1);
      const group2Rows = appendixTotals.filter((row) => Number(row.group) === 2);
      const group1Appendices = group1Rows.map((row) => String(row.appendix ?? ""));
      const group2Appendices = group2Rows.map((row) => String(row.appendix ?? ""));

      const orderMap = new Map(
        classifications.map((item, index) => [item.classification, Number(item.order ?? index + 100)])
      );
      const classTotals = buildAppendixClassTotals(rawSales, classificationMapping).sort(
        (a, b) => (orderMap.get(a.classification) ?? 100) - (orderMap.get(b.classification) ?? 100)
      );

      const formatMoneyM = (value) => ((Number(value) || 0) / 1000000).toFixed(1);
      const formatCount = (value) => new Intl.NumberFormat().format(Number(value) || 0);
      const formatMoney = (value) => `$${new Intl.NumberFormat().format(Number(value || 0).toFixed(0))}`;

      const getForAppendix = (items, appendix, valueKey) => {
        const row = items.find((entry) => String(entry.appendix ?? "") === String(appendix ?? ""));
        return row ? Number(row[valueKey] ?? 0) : null;
      };

      const sumForAppendices = (items, appendixList, valueKey) =>
        items
          .filter((entry) => appendixList.includes(String(entry.appendix ?? "")))
          .reduce((sum, entry) => sum + (Number(entry[valueKey] ?? 0) || 0), 0);

      const group1AmountTotal = group1Rows.reduce((sum, row) => sum + (Number(row.saleTotalPrice) || 0), 0);
      const group2AmountTotal = group2Rows.reduce((sum, row) => sum + (Number(row.saleTotalPrice) || 0), 0);
      const group1CountTotal = group1Rows.reduce((sum, row) => sum + (Number(row.saleCount) || 0), 0);
      const group2CountTotal = group2Rows.reduce((sum, row) => sum + (Number(row.saleCount) || 0), 0);

      const headerCells = appendixTotals
        .map(
          (row) =>
            `<td VALIGN="BOTTOM" BGCOLOR="#525051"><font FACE="Arial" SIZE="1" COLOR="#FFFFFF"><p ALIGN="CENTER">${
              showAppendixNumber ? `${row.appendix}<br/>` : ""
            }${row.issue_date ?? ""}</p></font></td>`
        )
        .join("\n");

      const amountCells = appendixTotals
        .map(
          (row) =>
            `<td VALIGN="BOTTOM" BGCOLOR="#FFFFFF"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER">${formatMoneyM(
              row.saleTotalPrice
            )}</p></font></td>`
        )
        .join("\n");

      const countCells = appendixTotals
        .map(
          (row) =>
            `<td VALIGN="BOTTOM" BGCOLOR="#FFFFFF"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER">${formatCount(
              row.saleCount
            )}</p></font></td>`
        )
        .join("\n");

      let classRowsHtml = "";
      classTotals.forEach((classTotal, index) => {
        const classConfig = classifications.find((item) => item.classification === classTotal.classification);
        const classLabel = classConfig?.alternate_text || classTotal.classification;
        const bgColor = index % 2 === 0 ? "#EAEAEA" : "#FFFFFF";

        const renderClassRow = (label, items, valueKey, formatter, groupFormatter = formatter) => {
          const bodyCells = appendixTotals
            .map((appendixRow) => {
              const value = getForAppendix(items, appendixRow.appendix, valueKey);
              const display = value === null ? "-" : formatter(value);
              return `<td VALIGN="BOTTOM" BGCOLOR="${bgColor}"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER">${display}</p></font></td>`;
            })
            .join("\n");

          const group1Value = sumForAppendices(items, group1Appendices, valueKey);
          const group2Value = sumForAppendices(items, group2Appendices, valueKey);

          classRowsHtml += `<tr>\n
<td VALIGN="BOTTOM" BGCOLOR="${bgColor}"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER">${label}</p></font></td>\n
${bodyCells}\n
<td VALIGN="BOTTOM" BGCOLOR="${bgColor}"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER"><b>${groupFormatter(
            group1Value
          )}</b></p></font></td>\n
<td VALIGN="BOTTOM" BGCOLOR="${bgColor}"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER"><b>${groupFormatter(
            group2Value
          )}</b></p></font></td>\n
</tr>\n`;
        };

        renderClassRow(`<b>${classLabel}</b> building sales ($m)`, classTotal.building_sales, "saleTotalPrice", formatMoneyM);
        renderClassRow(`${classLabel} building sales (no)`, classTotal.building_sales, "saleCount", formatCount);
        renderClassRow(`${classLabel} unit sales ($m)`, classTotal.unit_sales, "saleTotalPrice", formatMoneyM);
        renderClassRow(`${classLabel} unit sales (no)`, classTotal.unit_sales, "saleCount", formatCount);

        if (classConfig?.detail_strata) {
          renderClassRow(`${classLabel} unit sales avg (no)*`, classTotal.strata_sales, "strataSaleCount", formatCount);

          const method1Formatter = (value) => formatMoney(value);
          renderClassRow(
            "Average price per sqm: method 1 (total sqm basis)**",
            classTotal.strata_sales,
            "strataSalePrice",
            (salePrice) => {
              const row = classTotal.strata_sales.find((item) => Number(item.strataSalePrice) === Number(salePrice));
              if (!row || !Number(row.strataUnitArea)) {
                return "-";
              }
              return method1Formatter(Number(row.strataSalePrice) / Number(row.strataUnitArea));
            },
            () => "-"
          );

          renderClassRow(
            "Average price per sqm: method 2 (units basis)***",
            classTotal.strata_sales,
            "strataPricePerSqm",
            (value) => formatMoney(value),
            (value) => formatMoney(value)
          );
        }
      });

      const otherClassifications = Object.keys(classificationMapping)
        .filter((key) => classificationMapping[key] === "Other")
        .join("; ");

      const selectedCityscopeLabels = selectedCityscopeOptions
        .map((selected) => {
          const matched = cityscopeOptions.find((option) => option.value === selected.value);
          return matched?.cityscope_name || matched?.label || selected.label || selected.value;
        })
        .filter(Boolean);
      const cityscopeLabel = selectedCityscopeLabels.join(", ") || selectedCityscope;

      const html = `<table BORDER="0" CELLSPACING="0" WIDTH="100%" style="width:100%; table-layout:fixed;">\n
<tr bgcolor="#B90016">\n
<td align="left" valign="middle" height="40">&nbsp;<IMG alt="" src="http://www.cityscopeonline.com.au/static/newsletter/CityscopeQuarterlySales.gif"></td>\n
<td colspan="${appendixTotals.length + 2}" align="right" valign="middle">&nbsp;<IMG alt="" src="http://www.cityscopeonline.com.au/static/newsletter/RPDataLogo02.gif"></td></tr>\n
<tr bgcolor="#525051"><td colspan="${appendixTotals.length + 3}"><font FACE="Arial" SIZE="2" COLOR="#ffffff"><b>&nbsp;&nbsp;&nbsp;${cityscopeLabel} Scorecard</b></font></td></tr>\n
<tr>\n
<td VALIGN="BOTTOM" BGCOLOR="#525051"><font FACE="Arial" SIZE="1" COLOR="#FFFFFF"><p ALIGN="CENTER">Three months ended</p></font></td>\n
${headerCells}\n
<td VALIGN="MIDDLE" BGCOLOR="#525051"><font FACE="Arial" SIZE="1" COLOR="#FFFFFF"><p ALIGN="CENTER"><b>Group 1 total</b></p></font></td>\n
<td VALIGN="MIDDLE" BGCOLOR="#525051"><font FACE="Arial" SIZE="1" COLOR="#FFFFFF"><p ALIGN="CENTER"><b>Group 2 total</b></p></font></td>\n
</tr>\n
<tr>\n
<td VALIGN="BOTTOM" BGCOLOR="#FFFFFF"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER">Total sales ($m)</p></font></td>\n
${amountCells}\n
<td VALIGN="BOTTOM" BGCOLOR="#FFFFFF"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER"><b>${formatMoneyM(
        group1AmountTotal
      )}</b></p></font></td>\n
<td VALIGN="BOTTOM" BGCOLOR="#FFFFFF"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER"><b>${formatMoneyM(
        group2AmountTotal
      )}</b></p></font></td>\n
</tr>\n
<tr>\n
<td VALIGN="BOTTOM" BGCOLOR="#FFFFFF"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER">Total sales (no.)</p></font></td>\n
${countCells}\n
<td VALIGN="BOTTOM" BGCOLOR="#FFFFFF"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER"><b>${formatCount(
        group1CountTotal
      )}</b></p></font></td>\n
<td VALIGN="BOTTOM" BGCOLOR="#FFFFFF"><font FACE="Arial" SIZE="1" COLOR="#000000"><p ALIGN="CENTER"><b>${formatCount(
        group2CountTotal
      )}</b></p></font></td>\n
</tr>\n
${classRowsHtml}

<tr><td colspan="${appendixTotals.length + 3}"><br><p align="center"><font SIZE="1">"Other" sales include properties classified - ${
        otherClassifications || "None"
      }.</font></p></td></tr>\n
</table><br/>\n`;

      const today = new Date();
      const dd = String(today.getDate());
      const mm = String(today.getMonth() + 1);
      const yyyy = String(today.getFullYear());
      const fileName = `${selectedCityscope || "all"}_intropgx_${yyyy}${mm}${dd}.html`;

      const file = new File([new Blob([html], { type: "text/html", endings: "native" })], fileName, {
        type: "text/html",
      });

      setGeneratedFileName(fileName);
      setGeneratedHtml(html);

      try {
        const attachmentPath = await s3Upload(file, "public/article/intropgx");
        const url = await resolveUploadedNewsletterUrl(attachmentPath, fileName);
        setGeneratedUrl(url);
      } catch (uploadError) {
        console.error("Failed to upload newsletter preview to S3", uploadError);
        const localPreviewUrl = URL.createObjectURL(file);
        setGeneratedUrl(localPreviewUrl);

        const statusCode = getErrorStatusCode(uploadError);
        const isForbidden = statusCode === 403 || /forbidden|accessdenied|not authorized/i.test(String(uploadError?.message || ""));
        const errorDetails = isForbidden
          ? "S3 access denied (403). This is likely a bucket/IAM permission issue for article/intropgx."
          : statusCode
          ? `S3 request failed with status ${statusCode}.`
          : "S3 request failed.";

        setUi({
          showAlert: true,
          errorMessage: `${errorDetails} Newsletter generated locally, and a local preview link is shown instead.`,
        });
      }
    } catch (error) {
      console.error("Failed to generate newsletter preview", error);
      setUi({
        showAlert: true,
        errorMessage: "Failed to generate newsletter file.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    appendixTotals,
    cityscopeOptions,
    classificationMapping,
    classifications,
    hasConsolidated,
    rawSales,
    selectedCityscopeOptions,
    selectedCityscope,
    showAppendixNumber,
  ]);

  const handleDownloadGeneratedHtml = useCallback(() => {
    if (!generatedHtml) {
      return;
    }

    const blob = new Blob([generatedHtml], { type: "text/html" });
    const objectUrl = URL.createObjectURL(blob);
    const downloadName = generatedFileName || "intropgx_newsletter.html";

    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = downloadName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    URL.revokeObjectURL(objectUrl);
  }, [generatedFileName, generatedHtml]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    document.title = "Cityscope Newsletter Production";
    loadCityscopes();
  }, [isAuthenticated, loadCityscopes]);

  useEffect(() => {
    loadAppendices();
  }, [loadAppendices]);

  const updateTableState = (rowIndex, columnId, value) => {
    setAppendixTotals((previous) =>
      previous.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value, modified: true } : row
      )
    );
  };

  const updateClassificationState = (rowIndex, columnId, value) => {
    setClassifications((previous) =>
      previous.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value, modified: true } : row
      )
    );
  };

  const saveRecord = async (_dataSet, record) => {
    setAppendixTotals((previous) =>
      previous.map((row) => {
        const sameRow =
          String(row.appendix ?? "") === String(record.appendix ?? "") &&
          String(row.group ?? "") === String(record.group ?? "");
        return sameRow ? { ...row, ...record, modified: false } : row;
      })
    );
    return { result: true };
  };

  const deleteRecord = (_dataSet, record) => {
    setAppendixTotals((previous) =>
      previous.filter(
        (row) =>
          !(
            String(row.appendix ?? "") === String(record.appendix ?? "") &&
            String(row.group ?? "") === String(record.group ?? "")
          )
      )
    );
  };

  if (!isAuthenticated) {
    return null;
  }

  const classificationColumns = [
    columnHelper.accessor("classification", {
      id: "classification",
      header: "Classification",
      cell: EditableCell,
      size: 220,
    }),
    columnHelper.accessor("make_detail_line", {
      id: "make_detail_line",
      header: "Own Detail Line",
      cell: EditableCell,
      size: 130,
      meta: { type: "checkbox" },
    }),
    columnHelper.accessor("order", {
      id: "order",
      header: "Order",
      cell: EditableCell,
      size: 90,
    }),
    columnHelper.accessor("detail_strata", {
      id: "detail_strata",
      header: "Detail Strata",
      cell: EditableCell,
      size: 120,
      meta: { type: "checkbox" },
    }),
    columnHelper.accessor("alternate_text", {
      id: "alternate_text",
      header: "Alternate Text",
      cell: EditableCell,
      size: 220,
    }),
  ];

  return (
    <div className="intropgx-detail-page mt-3">
      <div className="intropgx-detail-shell">
      <Alert
        show={ui.showAlert}
        variant="danger"
        onClose={() => setUi({ ...ui, showAlert: false })}
        dismissible
      >
        {ui.errorMessage}
      </Alert>

      <Row className="intropgx-action-row align-items-end">
        <Col sm={12} md={6} lg={3}>
          <Form.Group className="intropgx-quarter-toggle" controlId={createId("search", "quarter", "checkbox")}>
            <Form.Check
              className="intropgx-quarter-check"
              checked={searchByQuarter}
              onChange={(event) => {
                setSearchByQuarter(event.target.checked);
                setSelectedAppendix("");
                setHasSalesLoaded(false);
                setHasConsolidated(false);
                setRawSales([]);
                setClassifications([]);
                setAppendixTotals([]);
                setClassificationMapping({});
              }}
              label="Search by quarter"
              type="checkbox"
              disabled={isMultiCityscopeSelection && searchByQuarter}
            />
          </Form.Group>
          <Select
            inputId={createId("cityscope", "select")}
            value={selectedCityscopeOptions}
            onChange={(selected) => {
              const selectedOptions = Array.isArray(selected) ? selected : [];
              setSelectedCityscopeOptions(selectedOptions);
              setSearchByQuarter(selectedOptions.length > 1);
              setAppendixOptions([]);
              setSelectedAppendix("");
              setHasSalesLoaded(false);
              setHasConsolidated(false);
              setRawSales([]);
              setClassifications([]);
              setAppendixTotals([]);
              setClassificationMapping({});
            }}
            options={cityscopeOptions}
            placeholder="Cityscope"
            isMulti
            closeMenuOnSelect={false}
            isClearable={true}
          />
        </Col>
        <Col sm={12} md={6} lg={2}>
          <Select
            inputId={createId("appendix", "select")}
            value={appendixOptions.find((option) => option.value === selectedAppendix) || null}
            onChange={(selected) => {
              setSelectedAppendix(selected?.value || "");
              setHasSalesLoaded(false);
              setHasConsolidated(false);
              setRawSales([]);
              setClassifications([]);
              setAppendixTotals([]);
              setClassificationMapping({});
            }}
            options={selectedCityscope ? appendixOptions : []}
            placeholder={selectedCityscope ? (searchByQuarter ? "Quarter" : "Appendix") : "No options"}
            noOptionsMessage={() => "No options"}
            isDisabled={!selectedCityscope}
            isClearable={false}
          />
        </Col>
        <Col sm={12} md={6} lg={3}>
          <Button className="intropgx-btn" id={createId("sales", "source", "button")} onClick={loadSalesSource}>
            Get Sales Source
          </Button>
        </Col>
        <Col sm={12} md={6} lg={3}>
          <Button
            className="intropgx-btn"
            id={createId("consolidate", "button")}
            onClick={handleConsolidate}
            disabled={!hasSalesLoaded}
          >
            Consolidate "Other"
          </Button>
        </Col>
      </Row>

      {hasSalesLoaded && classifications.length > 0 && (
        <div className="custom-data-table" style={{ marginBottom: "16px" }}>
          <GenericTable
            columns={classificationColumns}
            data={classifications}
            section="intropgx-classification-list"
            elementIds={classificationTableIds}
            pageSize={PAGE_SIZES.newsLetter}
            enablePagination={true}
            state={{ sorting }}
            onSortingChange={setSorting}
            enableSortingRemoval={false}
            meta={{
              updateData: updateClassificationState,
              handleSave: async () => ({ result: true }),
            }}
          />
        </div>
      )}

      {hasConsolidated && appendixTotals.length > 0 && (
        <Form.Group controlId={createId("show", "appendix", "checkbox")} className="intropgx-appendix-header-toggle">
          <Form.Check
            checked={showAppendixNumber}
            onChange={(event) => setShowAppendixNumber(event.target.checked)}
            label="Include Appendix # Header"
            type="checkbox"
          />
        </Form.Group>
      )}

      {hasConsolidated && (
        <AppendixList
          data={appendixTotals}
          isLoading={isLoading}
          isAuthenticated={isAuthenticated}
          controlType="table"
          saveRecord={saveRecord}
          deleteRecord={deleteRecord}
          updateTableState={updateTableState}
          showAlert={ui.showAlert}
          errorMessage={ui.errorMessage}
          onDismissAlert={() => setUi((previous) => ({ ...previous, showAlert: false }))}
        />
      )}

      {hasConsolidated && (
        <div className="intropgx-view-action">
          <Button
            className="intropgx-btn"
            id={createId("view", "news-file", "button")}
            onClick={handleViewNewsFile}
            disabled={!hasConsolidated || appendixTotals.length === 0}
          >
            View News File
          </Button>
        </div>
      )}

      {hasConsolidated && Object.keys(classificationMapping).length > 0 && (
        <div style={{ marginTop: "8px", fontSize: "12px" }}>
          Other classifications: {Object.keys(classificationMapping)
            .filter((key) => classificationMapping[key] === "Other")
            .join("; ") || "None"}
        </div>
      )}

      {generatedUrl && (
        <div className="intropgx-preview-wrap">
          <h5 className="intropgx-preview-link">
            <Button variant="link" className="p-0" onClick={handleDownloadGeneratedHtml}>
              {generatedFileName}
            </Button>
          </h5>
          {generatedHtml && (
            <iframe
              title="Generated newsletter preview"
              srcDoc={generatedHtml}
              className="intropgx-preview-frame"
            />
          )}
        </div>
      )}
      </div>
    </div>
  );
}
